import { gql } from "@apollo/client";

// Get comprehensive P&L data for all trades
export const GET_PORTFOLIO_PNL = gql`
  query GetPortfolioPnL {
    trades {
      id
      symbol
      date
      side
      notes
      walletId
      fee
      feePercentage
      feeCurrency
      positions {
        id
        type
        quantity
        price
        date
        notes
        status
        totalCost
        realizedPnl
        fee
        feeCurrency
        createdAt
        updatedAt
      }
    }
  }
`;

// Get P&L for a specific trade
export const GET_TRADE_PNL = gql`
  query GetTradePnL($tradeId: Int!, $includeFees: Boolean = true) {
    trade(id: $tradeId) {
      id
      symbol
      date
      side
      notes
      walletId
      fee
      feePercentage
      feeCurrency
      positions {
        id
        type
        quantity
        price
        date
        notes
        status
        totalCost
        realizedPnl
        fee
        feeCurrency
        createdAt
        updatedAt
      }
    }
  }
`;

// Get performance metrics with optional date filtering
export const GET_PERFORMANCE_METRICS = gql`
  query GetPerformanceMetrics(
    $startDate: DateTime
    $endDate: DateTime
    $includeFees: Boolean = true
  ) {
    trades(where: { date: { gte: $startDate, lte: $endDate } }) {
      id
      symbol
      date
      side
      notes
      walletId
      fee
      feePercentage
      feeCurrency
      positions {
        id
        type
        quantity
        price
        date
        notes
        status
        totalCost
        realizedPnl
        fee
        feeCurrency
        createdAt
        updatedAt
      }
    }
  }
`;

// Get monthly performance breakdown
export const GET_MONTHLY_PERFORMANCE = gql`
  query GetMonthlyPerformance($startDate: DateTime!, $endDate: DateTime!) {
    trades(where: { date: { gte: $startDate, lte: $endDate } }) {
      id
      symbol
      date
      side
      fee
      positions {
        id
        type
        quantity
        price
        date
        status
        totalCost
        realizedPnL
        fee
      }
    }
  }
`;

// Get symbol performance breakdown
export const GET_SYMBOL_PERFORMANCE = gql`
  query GetSymbolPerformance($includeFees: Boolean = true) {
    trades {
      id
      symbol
      date
      side
      fee
      positions {
        id
        type
        quantity
        price
        date
        status
        totalCost
        realizedPnL
        fee
      }
    }
  }
`;

// Get current market prices for symbols (placeholder - would need real market data API)
export const GET_MARKET_PRICES = gql`
  query GetMarketPrices($symbols: [String!]!) {
    marketPrices(symbols: $symbols) {
      symbol
      price
      timestamp
      change
      changePercent
    }
  }
`;

// Subscription for real-time P&L updates
export const PNL_UPDATES_SUBSCRIPTION = gql`
  subscription OnPnLUpdate {
    pnlUpdate {
      tradeId
      positionId
      type
      oldValue {
        realizedPnL
        unrealizedPnL
        totalPnL
        totalCost
        marketValue
        returnPercentage
      }
      newValue {
        realizedPnL
        unrealizedPnL
        totalPnL
        totalCost
        marketValue
        returnPercentage
      }
      timestamp
    }
  }
`;

// Mutation to update current market prices (for testing/demo purposes)
export const UPDATE_MARKET_PRICE = gql`
  mutation UpdateMarketPrice($symbol: String!, $price: Float!) {
    updateMarketPrice(symbol: $symbol, price: $price) {
      symbol
      price
      timestamp
    }
  }
`;

// Query to get historical P&L data for charts
export const GET_HISTORICAL_PNL = gql`
  query GetHistoricalPnL(
    $startDate: DateTime!
    $endDate: DateTime!
    $interval: String = "1d"
  ) {
    historicalPnL(
      startDate: $startDate
      endDate: $endDate
      interval: $interval
    ) {
      date
      realizedPnL
      unrealizedPnL
      totalPnL
      totalCost
      marketValue
      returnPercentage
    }
  }
`;

// Query to get drawdown analysis
export const GET_DRAWDOWN_ANALYSIS = gql`
  query GetDrawdownAnalysis($startDate: DateTime, $endDate: DateTime) {
    drawdownAnalysis(startDate: $startDate, endDate: $endDate) {
      peak
      trough
      drawdown
      recoveryDate
      duration
    }
  }
`;

// Query to get risk metrics
export const GET_RISK_METRICS = gql`
  query GetRiskMetrics($startDate: DateTime, $endDate: DateTime) {
    riskMetrics(startDate: $startDate, endDate: $endDate) {
      sharpeRatio
      sortinoRatio
      maxDrawdown
      var95
      cvar95
      volatility
      beta
    }
  }
`;
